import { get } from 'lodash';
import React, { ChangeEvent, useLayoutEffect, useState } from 'react';
import { FieldError, useController } from 'react-hook-form';
import { Change } from '../../../../types/Form';
import { isEqualDefined } from '../../../common/src/utils/commonUtils';
import { useFlowsDispatch } from '../../../common/src/utils/globalVariables';
import { hasSubGroups } from '../config/flowsStructureConfig';
import {
  flowIdAskaTravelOffer,
  flowIdShortCheckupCompanyBE,
  flowIdShortCheckupCompanyNL,
} from '../constants/flowIds';
import {
  checkQuestionnaire,
  updateAnswers,
} from '../store/redux/slices/flowsSlice';
import { FlowsQuestion } from '../types/FlowsTypes';
import useAnswerForQuestion from './useAnswerForQuestion';
import useCurrentGroup from './useCurrentGroup';
import useFlowsHelper from './useFlowsHelper';
import { useFlowsProgression } from './useFlowsProgression';
import { useFlowsRouteParams } from './useFlowsRouteParams';

interface Params {
  question: FlowsQuestion;
  defaultValue?: unknown;
}

// Typeguard that can be removed once old design system is removed
const isChangeEvent = (
  event: Change | ChangeEvent<HTMLInputElement>,
): event is ChangeEvent<HTMLInputElement> =>
  (event as ChangeEvent<HTMLInputElement>).target?.value != null;

// Disabled since return type would add unneeded complexity and is already available
// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
const useFlowInputComponent = ({ question, defaultValue }: Params) => {
  const { flowId } = useFlowsRouteParams();
  const { id, rules, index } = question;
  const { getGroupByQuestionId } = useFlowsHelper();
  const { saveSingleFieldDebounced } = useFlowsProgression();
  const getAnswerForQuestion = useAnswerForQuestion();
  /** Fixes modal prefilling for table input. Valid usecase, see -> https://react-hook-form.com/docs/usecontroller#:~:text=%7D-,Tips,-It%27s%20important%20to */
  const [valueState, setValueState] = useState<unknown>();

  const dispatch = useFlowsDispatch();
  const parentCurrentGroup = useCurrentGroup();
  const currentGroup = getGroupByQuestionId(id);
  const currentQuestionAnswer = getAnswerForQuestion(id, index);

  const {
    field: { value, name, onChange, onBlur },
    formState: { errors, ...otherFormState },
    fieldState: { isTouched, ...otherFieldState },
    ...otherProps
  } = useController({ name: id, defaultValue });

  useLayoutEffect(() => {
    // If no answer is given yet pick the question value OR the defaultValue given
    if (!currentQuestionAnswer) {
      if (question.value != null) persistAnswer(question.value, true);
      else if (defaultValue != null) persistAnswer(defaultValue, true);
    }
  }, [defaultValue, question.value]);

  useLayoutEffect(() => {
    // If the input has a default value and the answer is null, make sure we set it
    // on the default value instead, as otherwise it might crash as the assumption is that
    // default is will always be the defaultValue.
    if (defaultValue != null && currentQuestionAnswer?.answer == null) {
      handleOnChange({ name: id, value: defaultValue });
    } else {
      handleOnChange({ name: id, value: currentQuestionAnswer?.answer });
    }
  }, [currentQuestionAnswer]);

  const getAnswer = (e: Change | React.ChangeEvent<HTMLInputElement>) =>
    isChangeEvent(e) ? e.target.value : e.value;

  const persistAnswer = (answer: unknown, prefilled = false) => {
    dispatch(
      updateAnswers({
        id: id,
        answer:
          rules.data_type?.toLowerCase() === 'number' ? Number(answer) : answer,
        groupId: currentGroup.id,
        tag: parentCurrentGroup.id,
        prefilled,
        index: index,
      }),
    );

    // Do some extra checks after a question has been answered (like conditions)
    dispatch(checkQuestionnaire());
  };

  /**
   * Changes the value but does *not* persist to the redux state for the input.
   * @param e The event which can be an HTML event or self-defined WeGroup Change event
   */
  const handleOnChange = (e: Change | React.ChangeEvent<HTMLInputElement>) => {
    onChange(getAnswer(e));
    setValueState(getAnswer(e));
    const name = isChangeEvent(e) ? e.currentTarget.name : e.name;
    if (document.activeElement?.['name'] !== name) handleOnBlur(e);
  };

  /**
   * Changes the value and persists the value to the redux state for the input
   * @param e The event which can be an HTML event or self-defined WeGroup Change event
   */
  const handleOnBlur = (e: Change | React.ChangeEvent<HTMLInputElement>) => {
    let answer = getAnswer(e);

    if (rules.data_type?.toLowerCase() === 'number' && answer === '') {
      answer = undefined;
    }

    if (!isEqualDefined(answer, currentQuestionAnswer?.answer)) {
      persistAnswer(answer);
      onChange(answer === undefined ? null : answer);
      setValueState(answer === undefined ? null : answer);
      /** When a flow uses the TABLE component, the duplicate questions shouldn't be shown in the flow (but in a table) */
      const noDuplicatedQuestionFlows = [
        flowIdShortCheckupCompanyNL,
        flowIdShortCheckupCompanyBE,
        flowIdAskaTravelOffer,
      ];
      if (
        hasSubGroups(flowId) ||
        noDuplicatedQuestionFlows.includes(flowId || '')
      ) {
        saveSingleFieldDebounced(id, index);
      }
    }

    onBlur();
  };

  return {
    field: {
      value: defaultValue && value == null ? defaultValue : value || valueState,
      name,
      onChange: handleOnChange,
      onBlur: handleOnBlur,
    },
    error: get(errors, question.id) as FieldError | undefined,
    prefilled: question.rules.is_disabled
      ? false
      : currentQuestionAnswer != null
      ? currentQuestionAnswer?.prefilled
      : question.value != null || defaultValue != null,
    formState: { errors, ...otherFormState },
    fieldState: { isTouched, ...otherFieldState },
    ...otherProps,
  };
};

export default useFlowInputComponent;
